<template>
	<Modal
		size="md"
		frameless
		:hideFooter="true"
		:panelClass="panelClass"
		:documentClass="documentClass"
		@close="$emit('close')"
	>
		<div class="flex w-full flex-col items-start self-stretch">
			<div class="flex w-full items-center justify-between self-stretch px-6 pb-0 pt-6">
				<h2 class="m-0 text-[24px] font-extrabold uppercase leading-[32px] tracking-[0.3px] text-[#262626] font-heading">
					{{ $t('cookieConsent.settings.title') }}
				</h2>
				<Button
					type="tertiary"
					icon="times"
					icon-size="lg"
					class="h-10 w-10 !px-0 rounded-[8px]"
					:aria-label="$t('crud.close')"
					@click="$emit('close')"
				/>
			</div>

			<div class="flex max-h-[630px] flex-col items-start gap-6 overflow-auto p-6 self-stretch">
				<div class="flex w-full shrink-0 flex-col items-start gap-4 self-stretch">
					<div class="flex w-full shrink-0 flex-col items-start self-stretch rounded-[8px] border border-[#E3E3E3] p-4">
						<div class="flex w-full items-start justify-between">
							<div class="min-w-0 flex-1">
								<p class="m-0 self-stretch text-[16px] font-medium leading-[20.8px] tracking-[0.3px] text-[#303030]">
									{{ $t('cookieConsent.categories.essential.title') }}
								</p>
								<p class="m-0 mt-1 self-stretch text-[14px] font-normal leading-[20px] tracking-[0.3px] text-[#5A5A5A]">
									{{ $t('cookieConsent.categories.essential.description') }}
								</p>
							</div>
							<div class="flex flex-col items-start pb-4">
								<Toggle
									:name="'cookie-consent-essential'"
									:value="true"
									:disabled="true"
								/>
							</div>
						</div>
					</div>

					<div class="flex w-full shrink-0 flex-col items-start self-stretch rounded-[8px] border border-[#E3E3E3] p-4">
						<div class="flex w-full items-start justify-between">
							<div class="min-w-0 flex-1">
								<p class="m-0 self-stretch text-[16px] font-medium leading-[20.8px] tracking-[0.3px] text-[#303030]">
									{{ $t('cookieConsent.categories.preferences.title') }}
								</p>
								<p class="m-0 mt-1 self-stretch text-[14px] font-normal leading-[20px] tracking-[0.3px] text-[#5A5A5A]">
									{{ $t('cookieConsent.categories.preferences.description') }}
								</p>
							</div>
							<div class="flex flex-col items-start pb-4">
								<Toggle v-model="preferences" :name="'cookie-consent-preferences'" />
							</div>
						</div>
					</div>

					<div class="flex w-full shrink-0 flex-col items-start self-stretch rounded-[8px] border border-[#E3E3E3] p-4">
						<div class="flex w-full items-start justify-between">
							<div class="min-w-0 flex-1">
								<p class="m-0 self-stretch text-[16px] font-medium leading-[20.8px] tracking-[0.3px] text-[#303030]">
									{{ $t('cookieConsent.categories.statistics.title') }}
								</p>
								<p class="m-0 mt-1 self-stretch text-[14px] font-normal leading-[20px] tracking-[0.3px] text-[#5A5A5A]">
									{{ $t('cookieConsent.categories.statistics.description') }}
								</p>
							</div>
							<div class="flex flex-col items-start pb-4">
								<Toggle v-model="statistics" :name="'cookie-consent-statistics'" />
							</div>
						</div>
					</div>

					<div class="flex w-full shrink-0 flex-col items-start self-stretch rounded-[8px] border border-[#E3E3E3] p-4">
						<div class="flex w-full items-start justify-between">
							<div class="min-w-0 flex-1">
								<p class="m-0 self-stretch text-[16px] font-medium leading-[20.8px] tracking-[0.3px] text-[#303030]">
									{{ $t('cookieConsent.categories.marketing.title') }}
								</p>
								<p class="m-0 mt-1 self-stretch text-[14px] font-normal leading-[20px] tracking-[0.3px] text-[#5A5A5A]">
									{{ $t('cookieConsent.categories.marketing.description') }}
								</p>
							</div>
							<div class="flex flex-col items-start pb-4">
								<Toggle v-model="marketing" :name="'cookie-consent-marketing'" />
							</div>
						</div>
					</div>
				</div>

				<div class="flex items-center justify-between self-stretch gap-x-3 gap-y-2 flex-wrap">
					<Button
						type="secondary"
						:disabled="busy"
						@click="handleRejectAll"
					>
						{{ $t('cookieConsent.settings.rejectAll') }}
					</Button>
					<Button
						type="secondary"
						class="mr-auto"
						:disabled="busy"
						@click="handleAcceptAll"
					>
						{{ $t('cookieConsent.settings.acceptAll') }}
					</Button>
					<Button
						type="primary"
						:disabled="busy"
						@click="handleSave"
					>
						{{ $t('cookieConsent.settings.save') }}
					</Button>
				</div>
			</div>
		</div>
	</Modal>
</template>

<script>
import Modal from '~/components/ui/layout/Modal'
import Toggle from '~/components/ui/form/Toggle'
import Button from '~/components/ui/common/Button'

export default {
	name: 'CookieSettingsModal',
	components: {
		Modal,
		Toggle,
		Button
	},
	data() {
		return {
			busy: false,
			preferences: false,
			statistics: false,
			marketing: false
		}
	},
	computed: {
		documentClass() {
			// Match Figma: inline-flex container that doesn't stretch to max width unnecessarily
			// (still constrained by Modal `size` max-width)
			return 'inline-flex w-auto'
		},
		panelClass() {
			return [
				// Match Figma: display:inline-flex; max-width:672px; flex-direction:column; align-items:flex-start
				'inline-flex',
				'flex-col',
				'items-start',
				// Figma: min-width 672 on desktop, responsive on mobile
				'w-full',
				'max-w-[672px]',
				'md:min-w-[672px]',
				'bg-white',
				'overflow-hidden',
				'max-h-none',
				'rounded-[12px]',
				'shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)]',
				// Don't force a min-width; keep it responsive on small screens
			].join(' ')
		}
	},
	mounted() {
		this.syncFromStore()
	},
	methods: {
		syncFromStore() {
			this.preferences = this.$cookieConsent.isAllowed('preferences')
			this.statistics = this.$cookieConsent.isAllowed('statistics')
			this.marketing = this.$cookieConsent.isAllowed('marketing')
		},
		async handleRejectAll() {
			this.busy = true
			try {
				await this.$store.dispatch('cookieConsent/rejectAll')
				this.$emit('close')
			} finally {
				this.busy = false
			}
		},
		async handleAcceptAll() {
			this.busy = true
			try {
				await this.$store.dispatch('cookieConsent/acceptAll')
				this.$emit('close')
			} finally {
				this.busy = false
			}
		},
		async handleSave() {
			this.busy = true
			try {
				await this.$store.dispatch('cookieConsent/saveCustom', {
					categories: {
						preferences: this.preferences,
						statistics: this.statistics,
						marketing: this.marketing
					}
				})
				this.$emit('close')
			} finally {
				this.busy = false
			}
		}
	}
}
</script>
